import { StepTrackerStatus } from '@/components/navigation/StepTracker/StepTracker';

/**
 * The steps of the SmartReview "Client onboarding" flow, in display order.
 */
export enum SmartReviewStep {
  ClientSetup = 'ClientSetup',
  DocumentUpload = 'DocumentUpload',
  SmartReviewOption = 'SmartReviewOption',
  Presentation = 'Presentation',
}

/** URL-backed full-screen steps that branch from the onboarding wizard. */
export enum SmartReviewOnboardingSection {
  NoDocumentsPlan = 'noDocumentsPlan',
}

/** Ordered list of steps; drives the step tracker and next/back navigation. */
export const SMART_REVIEW_STEP_ORDER: SmartReviewStep[] = [
  SmartReviewStep.ClientSetup,
  SmartReviewStep.DocumentUpload,
  SmartReviewStep.SmartReviewOption,
  SmartReviewStep.Presentation,
];

export function getSmartReviewStepOrder(
  isSmartReviewEnabled: boolean
): SmartReviewStep[] {
  if (isSmartReviewEnabled) return SMART_REVIEW_STEP_ORDER;
  return SMART_REVIEW_STEP_ORDER.filter(
    (step) => step !== SmartReviewStep.SmartReviewOption
  );
}

/** Labels shown in the step tracker for each step. */
export const SMART_REVIEW_STEP_LABELS: Record<SmartReviewStep, string> = {
  [SmartReviewStep.ClientSetup]: 'Basic info',
  [SmartReviewStep.DocumentUpload]: 'Documents',
  [SmartReviewStep.SmartReviewOption]: 'SmartReview',
  [SmartReviewStep.Presentation]: 'Presentation',
};

/**
 * Derive a step's tracker status from its position relative to the active step:
 * past steps are Complete, the current step is Active, and future steps are
 * Inactive.
 */
export function getStepStatus(
  stepIndex: number,
  activeIndex: number
): StepTrackerStatus {
  if (stepIndex < activeIndex) return StepTrackerStatus.Complete;
  if (stepIndex === activeIndex) return StepTrackerStatus.Active;
  return StepTrackerStatus.Inactive;
}

/**
 * Props shared by every step's Footer. The parent modal owns step state and
 * passes these navigation callbacks down; each step decides which controls to
 * render (e.g. Previous, Continue).
 */
export interface SmartReviewStepFooterProps {
  /** Advance to the next step. No-op on the last step. */
  onAdvance: () => void | Promise<void>;
  /** Return to the previous step. No-op on the first step. */
  onBack: () => void;
  /** Close (and reset) the whole flow. */
  onClose: () => void;
  isFirstStep: boolean;
  isLastStep: boolean;
}
